import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { JWTTokenService } from 'src/app/services/jwt-token.service';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormBuilder } from '@angular/forms';
import { LocalStorageService } from 'src/app/infrastructure/storage/local-storage.service';
import { ConfigService } from 'src/app/services/config.service';
import { IconDirective } from '@coreui/icons-angular';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { LoggerService } from 'src/app/services/logger.service';
import { AuthStateService } from 'src/app/services/auth-state.service';
import { CustomValidators } from 'src/app/validators/custom-validators';

import { AuthService } from 'src/app/services/auth.service';

@Component({
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    IconDirective,
    RouterModule,
  ],
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit, OnDestroy {
  public loading = false;
  public logoPath: string = 'assets/img/brand/LOGO.png';
  public appTitle: string = 'Admin';
  public passwordVisible: boolean = false;
  public showTotpStep = false;
  public totpCode = '';
  public totpLoading = false;

  public togglePasswordVisibility(): void {
    this.passwordVisible = !this.passwordVisible;
  }

  public connectForm = this.formBuilder.group({
    email: [
      '',
      [Validators.required, CustomValidators.email(), CustomValidators.noXSS()],
    ],
    password: ['', [Validators.required, Validators.minLength(6)]],
    keepConnection: [false],
  });

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly authService: AuthService,
    private readonly jwtService: JWTTokenService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly formBuilder: FormBuilder,
    private readonly storage: LocalStorageService,
    private readonly configService: ConfigService,
    private readonly toastr: ToastrService,
    private readonly logger: LoggerService,
    private readonly cdr: ChangeDetectorRef,
    private readonly authState: AuthStateService,
  ) {}

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Submit forms to api.
  async onSubmitConnect() {
    const { email, password, keepConnection } = this.connectForm.value;

    // keep user decision in storage (for refresh token)
    this.storage.setItem('keepConnection', keepConnection);

    this.loading = true;
    this.authService
      .login(email || '', password || '')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.loading = false;

          // Second factor required: hold off on the role check/navigation until
          // the TOTP code is verified — the session isn't fully authorized yet.
          if (response.totp_required) {
            this.showTotpStep = true;
            this.cdr.markForCheck();
            return;
          }

          this.completeLogin(email || '');
        },
        error: (_error) => {
          this.loading = false;
          // Token clearing is handled in AuthService logout or needs to be manual here if specific
          this.jwtService.setToken(null);
          this.toastr.error(
            'Vérifiez vos identifiants et réessayez.',
            'Échec de la connexion',
            { timeOut: 5000, progressBar: true },
          );
          this.cdr.markForCheck();
        },
      });
  }

  submitTotpCode(): void {
    if (!this.totpCode || this.totpCode.length !== 6 || this.totpLoading) {
      return;
    }

    const { email } = this.connectForm.value;
    this.totpLoading = true;
    this.authService
      .verifyTotp(this.totpCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.totpLoading = false;
          this.showTotpStep = false;
          this.completeLogin(email || '');
        },
        error: () => {
          this.totpLoading = false;
          this.toastr.error(
            'Code invalide ou expiré. Réessayez.',
            'Échec de la vérification',
            { timeOut: 5000, progressBar: true },
          );
          this.cdr.markForCheck();
        },
      });
  }

  cancelTotp(): void {
    this.showTotpStep = false;
    this.totpCode = '';
    this.authState.clearAuthState();
    this.jwtService.setToken(null);
  }

  /**
   * Verify user has ROLE_SUPER_ADMIN before allowing access, then navigate in.
   * With httpOnly cookies, JWT is not accessible to JS — read from AuthStateService instead.
   */
  private completeLogin(email: string): void {
    const userInfo = this.authState.getUserInfo();
    const userRoles = userInfo?.roles || [];

    if (!Array.isArray(userRoles) || !userRoles.includes('ROLE_SUPER_ADMIN')) {
      // User authenticated but not super admin - reject access
      this.authState.clearAuthState();
      this.jwtService.setToken(null);
      this.toastr.error(
        'Accès réservé aux super-administrateurs.',
        'Accès refusé',
        { timeOut: 5000, progressBar: true },
      );
      this.logger.warn(
        'Non-super-admin user attempted to access admin interface',
        { email, roles: userRoles },
      );
      this.cdr.markForCheck();
      return;
    }

    // Redirect to returnUrl if provided, otherwise go to home
    const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
    this.router.navigateByUrl(returnUrl);
    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    // With httpOnly cookies, check authentication state
    if (this.authState.isAuthenticated()) {
      this.router.navigateByUrl('/');
      return;
    }

    // Clear any existing authentication state
    this.jwtService.setToken(null);
    this.authState.clearAuthState();

    try {
      this.logoPath = this.configService.get('logoPath');
      this.appTitle = this.configService.get('appTitle');
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_error) {
      this.logger.debug('Using default logo path and app title');
    }
  }
}
